import { NewDocumentService } from './../../services/document/new-document.service';
import { AfterViewInit, ChangeDetectorRef, Component, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef, ViewRef, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BroadcastService, ModifiedFieldService, select, ClientConstants } from '@formbird/services';
import { FormParameters, User } from '@formbird/types';
import { DocumentListService } from '../../services/document/document-list.service';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { FormColorDirective } from '../../directives/form-color.directive';
import { TemplateComponent } from '../template/template.component';
import { FormsModule } from '@angular/forms';
@Component({
    selector: 'ft-main-template',
    templateUrl: './main-template.component.html',
    styleUrls: ['./main-template.component.scss'],
    imports: [FormsModule, TemplateComponent, FormColorDirective]
})
export class MainTemplateComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private documentListService = inject(DocumentListService);
  private modifiedFieldService = inject(ModifiedFieldService);
  private broadcastService = inject(BroadcastService);
  private newDocumentService = inject(NewDocumentService);
  private cdr = inject(ChangeDetectorRef);


  @Input() params;

  @select(['userState', 'user']) user$: Observable<User>;

  @ViewChild('ngMainCont', { read: ViewContainerRef, static: false }) private viewContainerRef;
  @ViewChild('ngMainTpl', { read: TemplateRef, static: false }) private ngMainTpl: TemplateRef<any>;

  formParameters: FormParameters;
  user: any;
  subs: Subscription = new Subscription();
  queue = new Subject<any>();
  ngAfterViewInit(): void {
    this.renderForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params'] && !changes['params'].firstChange) {
      this.renderForm();
    }
  }

  ngOnInit() {

    const subUser = this.user$.subscribe(user => {
      this.user = user;
    });

    const renewDocSub = this.broadcastService.on(ClientConstants.DOCUMENT_RENEW).subscribe((data: any) => {
      if (this.newDocumentService.isNeedReloadForm()) {
        this.params = {
          id: data?.templateId
        };
        this.renderForm();
        
        this.newDocumentService.resetRenewFlag();
      }
    });

    this.subs.add(subUser);
    this.subs.add(renewDocSub);
  }

  /**
   * Intercepts clicks on anchor elements rendered inside custom web components (e.g. sc-static-html).
   * Uses composedPath() so links inside Shadow DOM custom elements are also caught.
   * External links and target="_blank" links are left to browser default behaviour.
   * @param event - The native MouseEvent from the click
   */
  @HostListener('click', ['$event'])
  handleLinkClick(event: MouseEvent): void {
    const path = event.composedPath() as Element[];
    const anchor = path.find((el) => el.tagName === 'A') as HTMLAnchorElement | undefined;
    if (!anchor) return;

    const href = anchor.getAttribute('href');
    if (!href || anchor.target === '_blank') return;

    const isExternal = /^(https?:\/\/|\/\/)/.test(href);
    if (!isExternal) {
      event.preventDefault();
      this.router.navigateByUrl(href.startsWith('/') ? href : '/' + href);
    }
  }

  renderForm() {
    this.clearViewContainer();
    this.processTemplateViewData();
  }

  clearViewContainer() {
    this.viewContainerRef?.clear();
  }

  processTemplateViewData() {

    // clearing already loaded documents before loading new one to avoid hanging around in the client memory
    this.modifiedFieldService.resetFormDirty();
    this.modifiedFieldService.resetModifiedFields();

    const documentId = this.params?.id;
    if (documentId) {
      // create the unsaved document group. This will be what is used to group these documents together.
      // These documents will be saved together, so you can have different groups of unsaved documents
      // that are saved in different ways. Eg. there will be a document group that is saved on clicking
      // the save button on the navbar. If there is a dialog opened with a document that is saved, it will
      // be in a different document group so it can be saved on closing of the dialog, while still keeping
      // the unsaved changes on the main form
      const mainDocumentListId = this.documentListService.setupDocumentListId(true);

      // the override template id can be specified as the second parameter in the URL:
      // http://localhost/form/<documentId>/<templateId>. See the formTemplateOverride route in
      // SharedUrlRoutes for further details
      const overrideId = this.params?.templateId;
      this.formParameters = {
        overrideTemplateId: overrideId,
        selectedDocumentId: documentId,
        unsavedDocumentListId: mainDocumentListId,
        isMainDoc: true
      };
    }

    this.appendMainTemplate();

    this.resetScroll();
  }

  appendMainTemplate() {

    this.viewContainerRef?.createEmbeddedView(this.ngMainTpl);

    this.detectChanges();
  }

  detectChanges() {
    if (!(this.cdr as ViewRef).destroyed) {
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  resetScroll() {
    (<any>window).document.getElementsByClassName('mat-drawer-content')[0].scrollTop = 0;
  }

}
